import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, CheckCircle, Share2, TrendingUp, Search, Layout,
  Users, Globe, Clock, Tv, MessageCircle, FileText, Mic, Layers,
  Code, Briefcase, Zap, Target, Heart, Star, Cpu, MapPin, Shield,
  ChevronUp, ChevronDown, Building2, Megaphone, Monitor,
  Award, Twitter, Facebook, Instagram,
} from "lucide-react";
import PortfolioNavbar from "./PortfolioNavbar";
import PortfolioFooter from "./PortfolioFooter";

/* ─── HERO SLIDES ─── */
const heroSlides = [
  {
    eyebrow: "Political & Corporate Campaigns",
    line1: "Build Brands",
    line2: "That Dominate",
    sub: "From state-level political campaigns to corporate PR — we deliver measurable sentiment shift backed by real analytics.",
    image: "/images/photo-1557804506-669a67965ba0.jpg",
    cta: { label: "Explore Our Work", path: "/case-studies" },
  },
  {
    eyebrow: "Pan-India Influencer Network",
    line1: "250+ Verified",
    line2: "Influencers",
    sub: "Coordinated influencer campaigns across Twitter, Instagram & Facebook with guaranteed top-3 trend placements.",
    image: "/images/photo-1611162617213-7d7a39e9b1d7.jpg",
    cta: { label: "Social Media Services", path: "/social-media" },
  },
  {
    eyebrow: "In-House Content Studio",
    line1: "Viral Content",
    line2: "in 15 Minutes",
    sub: "Hindi & English content — punchlines, press releases, AV assets. Pan-India organic reach at campaign speed.",
    image: "/images/photo-1533750349088-cd871a92f312.jpg",
    cta: { label: "Content & SEO", path: "/seo-content" },
  },
];

/* ─── DATA ─── */
const brandingStats = [
  { value: "250+", label: "Verified Influencers" },
  { value: "5", label: "Major States" },
  { value: "4000+", label: "Daily Engagements" },
  { value: "15 min", label: "Viral Content Speed" },
];

const serviceCards = [
  {
    icon: <Share2 className="w-6 h-6 text-purple-300" />,
    title: "Social Media Marketing",
    desc: "Full execution across Twitter, Instagram, Facebook & LinkedIn — 4000+ daily engagements, 250+ influencers.",
    path: "/social-media",
    image: "/images/photo-1432888498266-38ffec3eaf0a.jpg",
  },
  {
    icon: <TrendingUp className="w-6 h-6 text-purple-300" />,
    title: "Digital Marketing",
    desc: "Political campaigns, corporate PR and government outreach — backed by Brandwatch & Meltwater analytics.",
    path: "/digital-marketing",
    image: "/images/photo-1551288049-bebda4e38f71.jpg",
  },
  {
    icon: <Search className="w-6 h-6 text-purple-300" />,
    title: "SEO & Content Strategy",
    desc: "In-house Hindi & English content — punchlines, slogans, press releases, AV assets. Viral in under 15 minutes.",
    path: "/seo-content",
    image: "/images/photo-1499750310107-5fef28a66643.jpg",
  },
  {
    icon: <Layout className="w-6 h-6 text-purple-300" />,
    title: "UI/UX & Brand Identity",
    desc: "Corporate branding, thought leadership decks, PR & image consultancy — end-to-end reputation management.",
    path: "/uiux-branding",
    image: "/images/photo-1561070791-2526d30994b5.jpg",
  },
];

const coreExpertise = [
  {
    icon: <Users className="w-6 h-6 text-orange-500" />,
    title: "Team Size & Influence",
    detail: "A dynamic team of <strong>250 seasoned Twitter influencers</strong> ensuring broad digital coordination.",
  },
  {
    icon: <Globe className="w-6 h-6 text-orange-500" />,
    title: "Presence & Reach",
    detail: "Robust national presence with exceptional <strong>Pan-India organic reach</strong> across major platforms.",
  },
  {
    icon: <Clock className="w-6 h-6 text-orange-500" />,
    title: "Content Speed",
    detail: "Expert in creating viral content in <strong>under 15 minutes</strong>, maximizing trend capture.",
  },
  {
    icon: <Tv className="w-6 h-6 text-orange-500" />,
    title: "Campaign Impact",
    detail: "<strong>Consistently deliver impactful campaigns</strong> tailored for high-stakes political and corporate environments.",
  },
];

const strategicFocus = [
  {
    icon: <Megaphone className="w-5 h-5 text-purple-400" />,
    name: "Political Outreach",
    description: "High-volume, rapid-response campaigns for elections and government initiatives.",
  },
  {
    icon: <Building2 className="w-5 h-5 text-purple-400" />,
    name: "Corporate PR & Branding",
    description: "LinkedIn thought leadership, crisis management, and professional brand elevation.",
  },
  {
    icon: <Share2 className="w-5 h-5 text-purple-400" />,
    name: "Influencer Ecosystem",
    description: "Building, managing, and activating a verified Pan-India network for maximum reach.",
  },
  {
    icon: <Monitor className="w-5 h-5 text-purple-400" />,
    name: "Real-Time Monitoring",
    description: "Leveraging Brandwatch & Meltwater for live data analysis and sentiment tuning.",
  },
];

const teamPhilosophy = [
  {
    icon: <Award className="w-5 h-5 text-orange-500" />,
    title: "Collaborative Synergy",
    description: "Cross-functional teams integrating political analysis, digital marketing, and public relations to create cohesive strategies.",
  },
  {
    icon: <Cpu className="w-5 h-5 text-orange-500" />,
    title: "Data-Driven Decisions",
    description: "Every tactical choice informed by real-time analytics and predictive modeling, ensuring maximum impact and resource efficiency.",
  },
  {
    icon: <Zap className="w-5 h-5 text-orange-500" />,
    title: "Agile Adaptation",
    description: "We operate with flexibility, rapidly adjusting to shifting political landscapes and emerging digital trends.",
  },
];

const coreValues = [
  {
    icon: <Heart className="w-6 h-6 text-white" />,
    title: "Integrity & Trust",
    description: "Maintaining full transparency and ethical standards in all digital and on-ground operations.",
  },
  {
    icon: <Star className="w-6 h-6 text-white" />,
    title: "Innovation",
    description: "Constantly adapting strategies and tools to stay ahead of platform trends and client needs.",
  },
  {
    icon: <Briefcase className="w-6 h-6 text-white" />,
    title: "Accountability",
    description: "Taking ownership of results and providing measurable ROI on every campaign delivered.",
  },
];

const specializedServices = [
  {
    title: "Trend Management",
    detail: "Experts in managing international Twitter trends.",
    highlight: "Guarantee top-three placements for each managed trend.",
  },
  {
    title: "Influencer Marketing",
    detail: "Specialise in influencer marketing on both Twitter and Instagram.",
    highlight: "250+ verified influencers across all major platforms.",
  },
];

const workSummary = [
  {
    label: "Foundational Success",
    detail: "Spearheaded campaigns for <strong>MP VidhanSabha Elections</strong> for BJP MP & CMO MP, Maharashtra.",
  },
  {
    label: "Current Footprint",
    detail: "Providing high-impact digital services for major political initiatives across <strong>UP, Uttarakhand, and Rajasthan</strong>.",
  },
  {
    label: "Campaign Focus",
    detail: "Expertise in digital <strong>sentiment management</strong>, <strong>influencer coordination</strong>, and narrative amplification.",
  },
  {
    label: "Analytical Backbone",
    detail: "All campaigns backed by <strong>Brandwatch</strong> and <strong>Meltwater</strong> for real-time analysis.",
  },
];

const mindsetPoints = [
  {
    icon: <Code className="w-5 h-5 text-purple-400" />,
    title: "Entrepreneurial Drive",
    detail: "Team of young entrepreneurs with strong technical & management backgrounds.",
  },
  {
    icon: <TrendingUp className="w-5 h-5 text-purple-400" />,
    title: "Results-First Focus",
    detail: "Driven by a results-first mentality, committed to achieving measurable campaign goals.",
  },
  {
    icon: <Zap className="w-5 h-5 text-purple-400" />,
    title: "Continuous Learning",
    detail: "Cultivating a culture of continuous learning and agile problem-solving.",
  },
  {
    icon: <Layers className="w-5 h-5 text-purple-400" />,
    title: "Interdisciplinary Strength",
    detail: "Combining political strategy with cutting-edge digital execution.",
  },
];

const contentCreationSkills = [
  { text: "Social Media Campaigns (Twitter, Facebook, Instagram, LinkedIn)", icon: <Share2 className="w-4 h-4" /> },
  { text: "Influencer Marketing – Planning, Outreach & Execution", icon: <Users className="w-4 h-4" /> },
  { text: "PR Consultancy & Image Management", icon: <Target className="w-4 h-4" /> },
  { text: "Brand Campaigns (Concept to Delivery)", icon: <Zap className="w-4 h-4" /> },
  { text: "Punchline & Slogan Writing", icon: <Mic className="w-4 h-4" /> },
  { text: "Press Releases & Articles", icon: <FileText className="w-4 h-4" /> },
  { text: "Audio-Visual Content Creation", icon: <Tv className="w-4 h-4" /> },
  { text: "On-Ground Activations & Public Engagement", icon: <Briefcase className="w-4 h-4" /> },
  { text: "LinkedIn Campaigns for Corporate Clients", icon: <Building2 className="w-4 h-4" /> },
];

const corporateAndPR = [
  {
    title: "Corporate & LinkedIn Campaigns",
    points: [
      "Conducted LinkedIn Campaigns for multiple corporate clients",
      "Created and published thought leadership content, press notes & articles",
      "Developed audio-visual content and presentation decks for corporate branding",
      "Assisted clients with brand storytelling and professional positioning",
    ],
    icon: <Cpu className="w-6 h-6 text-white" />,
    color: "linear-gradient(45deg, #0077B5, #00A0DC)",
  },
  {
    title: "PR & Image Consultancy",
    points: [
      "Reputation Management & Crisis Handling",
      "Strategic PR Planning for government and corporate clients",
      "Press Releases, Articles & Media Briefs",
      "On-Ground PR Activations — events, public meetings, coverage support",
      "Image Consultancy for political figures and brands",
    ],
    icon: <Target className="w-6 h-6 text-white" />,
    color: "linear-gradient(45deg, #8A3AB9, #E95950)",
  },
];

const caseStudies = [
  {
    title: "Maharashtra CMO – Twitter Influencer Campaign",
    details: ["Handled 100 Top Influencers and Twitter Trends", "Conducted Positive & Counter Comments on designated accounts", "Delivered 3,000 Comments per day for engagement enhancement"],
    icon: <Twitter className="w-5 h-5 text-white" />,
    color: "linear-gradient(45deg, #1DA1F2, #0084B4)",
    image: "/images/photo-1611162618071-b39a2ec055fb.jpg",
  },
  {
    title: "UP CMO – Social Media Campaign",
    details: ["200 Influencer Tweets for state government initiatives", "Twitter Trends with high engagement results", "2,000 Positive Comments & 1,000 Counter Comments per day"],
    icon: <Facebook className="w-5 h-5 text-white" />,
    color: "linear-gradient(45deg, #3B5998, #8B9DC3)",
    image: "/images/photo-1512941937669-90a1b58e7e9c.jpg",
  },
  {
    title: "Uttarakhand CMO – Twitter & Instagram",
    details: ["Ongoing campaign for Uttarakhand CMO", "Managing 100 Influencers across Twitter & Instagram", "Performing Positive & Counter Comments daily on designated handles"],
    icon: <Instagram className="w-5 h-5 text-white" />,
    color: "linear-gradient(135deg, #8A3AB9, #E95950)",
    image: "/images/photo-1552733407-5d5c46c3bb3b.jpg",
  },
  {
    title: "Adani Mahakumbh – Instagram Campaign",
    details: ["Executed influencer collaboration with Mr. Gautam Adani", "Sent 4 Content Creators to Varanasi for on-ground content", "Delivered 15 Million+ Reach within 72 Hours"],
    icon: <Zap className="w-5 h-5 text-white" />,
    color: "linear-gradient(45deg, #FF8C00, #FF0000)",
    image: "/images/photo-1522202176988-66273c2fd55f.jpg",
  },
  {
    title: "MP CMO – 2023–24 Twitter Campaign",
    details: ["Managed 150 Top Influencers for positive outreach", "Delivered 2,000 Positive & Counter Comments per day", "Ensured coordinated narrative amplification"],
    icon: <Twitter className="w-5 h-5 text-white" />,
    color: "linear-gradient(45deg, #1DA1F2, #0084B4)",
    image: "/images/photo-1521737604893-d14cc237f11d.jpg",
  },
  {
    title: "Delhi BJP – 2024 Twitter Campaign",
    details: ["Managed 100 Influencers for coordinated Twitter communication", "Designed content strategy to boost engagement and visibility", "Focus on trending hashtags, narrative management & public perception"],
    icon: <Facebook className="w-5 h-5 text-white" />,
    color: "linear-gradient(45deg, #3B5998, #8B9DC3)",
    image: "/images/photo-1542744173-8e7e53415bb0.jpg",
  },
];

const impactMetrics = [
  { metric: "Pan-India Reach", description: "Proven capability to activate campaigns and manage sentiment across major states.", icon: <MapPin className="w-5 h-5 text-purple-400" /> },
  { metric: "Rapid Trend Capture", description: "Consistent record of making national trends in under 15 minutes.", icon: <TrendingUp className="w-5 h-5 text-purple-400" /> },
  { metric: "High Engagement Volumes", description: "Delivering thousands of organic comments and interactions daily.", icon: <MessageCircle className="w-5 h-5 text-purple-400" /> },
  { metric: "Crisis Mitigation", description: "Experienced in swift and effective counter-narrative deployment during sensitive events.", icon: <Shield className="w-5 h-5 text-purple-400" /> },
];

/* ─── HERO COMPONENT ─── */
const BrandingHero = () => {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const navigate = useNavigate();

  const goTo = useCallback((index) => {
    if (animating) return;
    setAnimating(true);
    setCurrent(index);
    setTimeout(() => setAnimating(false), 700);
  }, [animating]);

  const prev = () => goTo((current - 1 + heroSlides.length) % heroSlides.length);
  const next = useCallback(() => goTo((current + 1) % heroSlides.length), [current, goTo]);

  useEffect(() => {
    const t = setInterval(next, 5500);
    return () => clearInterval(t);
  }, [next]);

  return (
    <div className="relative h-screen min-h-[640px] bg-gray-950 overflow-hidden">
      {/* Ambient glow — no background image */}
      <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-purple-700/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-pink-600/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />

      {/* Slides */}
      {heroSlides.map((s, i) => (
        <div
          key={i}
          className="absolute inset-0 flex items-center"
          style={{ transform: `translateY(${(i - current) * 100}%)`, transition: "transform 0.7s cubic-bezier(0.77,0,0.18,1)", zIndex: i === current ? 10 : 5 }}
        >
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-16 w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">

              {/* LEFT — text content */}
              <div>
                <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-purple-500/20 border border-purple-400/40 mb-6">
                  <span className="w-2 h-2 rounded-full bg-purple-400 mr-2 animate-pulse" />
                  <span className="text-purple-300 text-xs font-black uppercase tracking-[0.2em]">{s.eyebrow}</span>
                </div>
                <h1 className="text-5xl sm:text-6xl font-black text-white leading-[1.05] mb-5">
                  {s.line1}<br />
                  <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">{s.line2}</span>
                </h1>
                <p className="text-white/55 text-base leading-relaxed mb-8 max-w-lg">{s.sub}</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button onClick={() => navigate(s.cta.path)} className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-black uppercase tracking-wider rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:opacity-90 transition-all duration-300 hover:scale-105 shadow-lg shadow-purple-500/30">
                    {s.cta.label} <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                  <button onClick={() => navigate("/contact")} className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-bold uppercase tracking-wider rounded-lg border border-white/20 text-white/70 hover:border-purple-400 hover:text-white transition-all duration-300">
                    Get in Touch
                  </button>
                </div>
              </div>

              {/* RIGHT — image panel */}
              <div className="hidden lg:block relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl group h-[480px]">
                  <img
                    src={s.image}
                    alt={s.line1}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading={i === 0 ? "eager" : "lazy"}
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-purple-900/60 via-transparent to-pink-500/10" />
                  {/* stat badge bottom-left */}
                  <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur-sm rounded-xl px-5 py-3 shadow-xl">
                    <p className="text-2xl font-black bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent">250+</p>
                    <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Verified Influencers</p>
                  </div>
                  {/* label badge top-right */}
                  <div className="absolute top-5 right-5 bg-purple-600/90 backdrop-blur-sm text-white rounded-full px-4 py-1.5 text-xs font-black tracking-wider shadow-lg">
                    {s.eyebrow}
                  </div>
                </div>
                {/* floating stat pill */}
                <div className="absolute -bottom-4 -right-4 bg-gray-950 border border-purple-500/30 rounded-2xl px-5 py-3 shadow-xl">
                  <p className="text-white text-sm font-black">Pan-India Reach</p>
                  <p className="text-purple-400 text-xs">5 Major States</p>
                </div>
              </div>

            </div>
          </div>
        </div>
      ))}

      {/* Vertical nav dots */}
      <div className="absolute right-6 sm:right-10 top-1/2 -translate-y-1/2 z-30 flex flex-col items-center gap-3">
        <button onClick={prev} className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-purple-500 hover:border-purple-500 transition-all duration-300">
          <ChevronUp className="w-4 h-4" />
        </button>
        <div className="flex flex-col items-center gap-2 py-1">
          {heroSlides.map((_, i) => (
            <button key={i} onClick={() => goTo(i)} className="rounded-full transition-all duration-300" style={{ width: "0.55rem", height: i === current ? "2rem" : "0.55rem", backgroundColor: i === current ? "#c084fc" : "rgba(255,255,255,0.3)" }} />
          ))}
        </div>
        <button onClick={next} className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-purple-500 hover:border-purple-500 transition-all duration-300">
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>

      {/* Slide counter */}
      <div className="absolute bottom-8 left-6 sm:left-16 z-30 flex items-end gap-1 text-white/30">
        <span className="text-4xl font-black text-white/70 leading-none">{String(current + 1).padStart(2, "0")}</span>
        <span className="text-lg mb-1">/</span>
        <span className="text-lg mb-1">{String(heroSlides.length).padStart(2, "0")}</span>
      </div>
    </div>
  );
};

/* ─── MAIN PAGE ─── */
const BrandingLanding = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-950 font-sans">
      <PortfolioNavbar />
      <BrandingHero />

      {/* ── Stats Strip ── */}
      <div className="bg-purple-950 border-y border-purple-800/40 py-12 px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {brandingStats.map((s) => (
            <div key={s.label}>
              <p className="text-3xl sm:text-4xl font-black bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent mb-1">{s.value}</p>
              <p className="text-xs font-bold uppercase tracking-widest text-white/40">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Our Branding Services ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-950">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.35em] text-center mb-3 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">What We Do</p>
          <h2 className="text-4xl sm:text-5xl font-black text-white text-center mb-4">Our Branding Services</h2>
          <p className="text-white/45 text-base text-center max-w-2xl mx-auto mb-14">
            End-to-end creative and performance marketing — from social media dominance to complete brand identity.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {serviceCards.map((card) => (
              <div key={card.title} onClick={() => navigate(card.path)} className="group relative bg-white/5 border border-white/10 rounded-2xl hover:border-purple-500/40 hover:-translate-y-2 transition-all duration-300 cursor-pointer overflow-hidden">
                <div className="relative h-44 overflow-hidden">
                  <img src={card.image} alt={card.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/40 to-transparent" />
                  <div className="absolute top-3 right-3 w-9 h-9 bg-purple-600/80 backdrop-blur-sm rounded-lg flex items-center justify-center border border-purple-400/30">{card.icon}</div>
                </div>
                <div className="p-5">
                  <h3 className="text-sm font-extrabold text-white mb-2">{card.title}</h3>
                  <p className="text-white/45 text-xs leading-relaxed mb-3">{card.desc}</p>
                  <div className="inline-flex items-center text-xs font-bold text-pink-400 group-hover:text-pink-300 transition-colors">
                    Learn More <ArrowRight className="w-3.5 h-3.5 ml-1.5 group-hover:translate-x-1 transition-transform duration-200" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Advanced Digital Capabilities ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-purple-950/50">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.35em] text-center mb-3 text-orange-400">Advanced Digital Capabilities</p>
          <h2 className="text-4xl font-black text-white text-center mb-4">Our Core Expertise</h2>
          <p className="text-white/45 text-base text-center max-w-xl mx-auto mb-14">
            Built on years of high-stakes campaign execution for government and corporate clients.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left — cards + reporting */}
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
                {coreExpertise.map((item) => (
                  <div key={item.title} className="bg-white/5 border border-white/10 rounded-2xl p-5 hover:border-orange-400/30 transition-colors duration-300">
                    <div className="w-10 h-10 bg-orange-500/15 rounded-xl flex items-center justify-center mb-3">{item.icon}</div>
                    <h3 className="text-sm font-extrabold text-white mb-1">{item.title}</h3>
                    <p className="text-white/50 text-xs leading-relaxed" dangerouslySetInnerHTML={{ __html: item.detail }} />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex items-start gap-3">
                  <div className="w-9 h-9 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0"><MessageCircle className="w-4 h-4 text-purple-400" /></div>
                  <div><h4 className="text-white font-bold text-sm mb-1">Engagement</h4><p className="text-white/50 text-xs">Over <span className="text-white font-semibold">4,000 comments daily</span> across managed accounts.</p></div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex items-start gap-3">
                  <div className="w-9 h-9 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0"><FileText className="w-4 h-4 text-purple-400" /></div>
                  <div><h4 className="text-white font-bold text-sm mb-1">Reporting</h4><p className="text-white/50 text-xs">Daily reports in <span className="text-white font-semibold">Excel & PDF</span> formats.</p></div>
                </div>
              </div>
            </div>
            {/* Right — image */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group h-[480px]">
              <img
                src="/images/photo-1579532537598-459ecdaf39cc.jpg"
                alt="Social media influencer marketing"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-purple-900/70 via-transparent to-pink-500/10" />
              <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur-sm rounded-xl px-5 py-3 shadow-xl">
                <p className="text-2xl font-black bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent">4000+</p>
                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Daily Engagements</p>
              </div>
              <div className="absolute top-5 right-5 bg-purple-600/90 text-white rounded-full px-4 py-1.5 text-xs font-black tracking-wider shadow-lg">
                Influencer Network
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Unique Market Differentiators ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-950">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.35em] text-center mb-3 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Unique Market Differentiators</p>
          <h2 className="text-4xl font-black text-white text-center mb-14">What Sets Us Apart</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left — image */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group h-[460px]">
              <img
                src="/images/photo-1529107386315-e1a2ed48a620.jpg"
                alt="Brand strategy and digital marketing"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-purple-900/65 via-transparent to-pink-900/30" />
              <div className="absolute bottom-5 right-5 bg-white/95 backdrop-blur-sm rounded-xl px-5 py-3 shadow-xl">
                <p className="text-2xl font-black text-purple-800">5</p>
                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Major States</p>
              </div>
              <div className="absolute top-5 left-5 bg-pink-600/90 text-white rounded-full px-4 py-1.5 text-xs font-black tracking-wider">
                Pan-India Reach
              </div>
            </div>
            {/* Right — focus cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {strategicFocus.map((f) => (
                <div key={f.name} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-purple-500/40 transition-colors duration-300">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center mb-4">{f.icon}</div>
                  <h3 className="text-white font-extrabold mb-2">{f.name}</h3>
                  <p className="text-white/45 text-sm leading-relaxed">{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Your Strategic Advantage ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-950 to-gray-950">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.35em] text-center mb-3 text-orange-400">Your Strategic Advantage</p>
          <h2 className="text-4xl font-black text-white text-center mb-4">Why Partner With Us</h2>
          <p className="text-white/45 text-base text-center max-w-xl mx-auto mb-14">
            Our approach combines political strategy, brand intelligence, and real-time execution capability.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mb-16">
            {/* Team Philosophy */}
            <div className="space-y-5">
              {teamPhilosophy.map((item) => (
                <div key={item.title} className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl p-5">
                  <div className="w-10 h-10 bg-orange-500/15 rounded-lg flex items-center justify-center flex-shrink-0">{item.icon}</div>
                  <div>
                    <h4 className="text-white font-extrabold mb-1">{item.title}</h4>
                    <p className="text-white/50 text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
            {/* Centre — image */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
              <img
                src="/images/photo-1600880292203-757bb62b4baf.jpg"
                alt="Team strategy and partnership"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 min-h-[360px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-950/80 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <p className="text-white font-black text-lg leading-tight">Data-Driven Campaign Execution</p>
                <p className="text-purple-300 text-xs mt-1">Real-time analytics via Brandwatch & Meltwater</p>
              </div>
            </div>
            {/* Impact Metrics */}
            <div className="space-y-5">
              {impactMetrics.map((item) => (
                <div key={item.metric} className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl p-5">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">{item.icon}</div>
                  <div>
                    <h4 className="text-white font-extrabold mb-1">{item.metric}</h4>
                    <p className="text-white/50 text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Core Values — with background image overlay */}
          <p className="text-xs font-black uppercase tracking-[0.35em] text-center mb-8 text-purple-400">Our Core Values</p>
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src="/images/photo-1540575467063-178a50c2df87.jpg"
              alt="Analytics and data"
              className="absolute inset-0 w-full h-full object-cover opacity-15"
            />
            <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6 p-8">
              {coreValues.map((v) => (
                <div key={v.title} className="bg-purple-900/60 backdrop-blur-sm border border-purple-500/30 rounded-2xl p-7 text-center">
                  <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/30">{v.icon}</div>
                  <h3 className="text-white font-extrabold text-lg mb-2">{v.title}</h3>
                  <p className="text-white/55 text-sm leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Strategy & Execution Blueprint ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.35em] text-center mb-3 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Strategy & Execution Blueprint</p>
          <h2 className="text-4xl font-black text-white text-center mb-14">Specialized Services</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
            {/* Left — service cards + work summary */}
            <div>
              <div className="space-y-5 mb-10">
                {specializedServices.map((s) => (
                  <div key={s.title} className="bg-white/5 border border-white/10 rounded-2xl p-7">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center"><Zap className="w-4 h-4 text-purple-400" /></div>
                      <h3 className="text-white font-extrabold text-lg">{s.title}</h3>
                    </div>
                    <p className="text-white/55 text-sm mb-3">{s.detail}</p>
                    <p className="text-pink-400 text-sm font-semibold italic">✦ {s.highlight}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs font-black uppercase tracking-[0.35em] mb-5 text-orange-400">Strategic Foundations</p>
              <div className="space-y-4">
                {workSummary.map((item) => (
                  <div key={item.label} className="bg-white/5 border border-white/10 rounded-2xl p-5 flex items-start gap-3">
                    <Target className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-white font-extrabold text-sm mb-1">{item.label}</h4>
                      <p className="text-white/50 text-xs" dangerouslySetInnerHTML={{ __html: item.detail }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Right — image */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group h-[500px]">
              <img
                src="/images/photo-1504868584819-f8e8b4b6d7e3.jpg"
                alt="Campaign analytics and strategy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-gray-950/80 via-purple-900/30 to-transparent" />
              <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur-sm rounded-xl px-5 py-3 shadow-xl">
                <p className="text-2xl font-black text-purple-800">250+</p>
                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Influencers Managed</p>
              </div>
              <div className="absolute top-5 right-5 bg-orange-500/90 text-white rounded-full px-4 py-1.5 text-xs font-black tracking-wider">
                Live Analytics
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Strategic Foundations (Mindset) ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-950">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.35em] text-center mb-3 text-purple-400">Our Team's Mindset</p>
          <h2 className="text-4xl font-black text-white text-center mb-14">Built to Win</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left — image */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group h-[420px]">
              <img
                src="/images/photo-1556761175-4b46a572b786.jpg"
                alt="Digital marketing team at work"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-gray-950/70 via-transparent to-purple-900/40" />
              <div className="absolute bottom-5 right-5 bg-white/95 backdrop-blur-sm rounded-xl px-5 py-3 shadow-xl">
                <p className="text-2xl font-black text-purple-800">15 min</p>
                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Viral Content Speed</p>
              </div>
              <div className="absolute top-5 left-5 bg-pink-600/90 text-white rounded-full px-4 py-1.5 text-xs font-black tracking-wider">
                In-House Team
              </div>
            </div>
            {/* Right — mindset cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {mindsetPoints.map((m) => (
                <div key={m.title} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-purple-400/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center mb-4">{m.icon}</div>
                  <h3 className="text-white font-extrabold mb-2">{m.title}</h3>
                  <p className="text-white/45 text-sm leading-relaxed">{m.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Creative Deliverables ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-purple-950/40">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.35em] text-center mb-3 text-orange-400">Creative Deliverables</p>
          <h2 className="text-4xl font-black text-white text-center mb-4">In-House Content Creation</h2>
          <p className="text-white/45 text-base text-center max-w-xl mx-auto mb-10">
            Multilingual content in Hindi & English — from concept to viral delivery.
          </p>
          {/* Image mosaic strip */}
          <div className="grid grid-cols-4 gap-3 mb-12 rounded-2xl overflow-hidden h-48">
            {[
              { src: "/images/photo-1516251193007-45ef944ab0c6.jpg", label: "Social Media" },
              { src: "/images/photo-1543286386-713bdd548da4.jpg", label: "Analytics" },
              { src: "/images/photo-1572021335469-31706a17aaef.jpg", label: "Branding" },
              { src: "/images/photo-1515187029135-18ee286d815b.jpg", label: "PR & Events" },
            ].map((img) => (
              <div key={img.label} className="relative overflow-hidden">
                <img src={img.src} alt={img.label} className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-950/70 to-transparent" />
                <p className="absolute bottom-2 left-2 text-white text-xs font-black">{img.label}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Skills list */}
            <div className="grid grid-cols-1 gap-3">
              {contentCreationSkills.map((s) => (
                <div key={s.text} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-5 py-3">
                  <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0 text-purple-300">{s.icon}</div>
                  <span className="text-white/70 text-sm">{s.text}</span>
                </div>
              ))}
            </div>
            {/* Corporate & PR cards */}
            <div className="space-y-6">
              {corporateAndPR.map((section) => (
                <div key={section.title} className="bg-white/5 border border-white/10 rounded-2xl p-7">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: section.color }}>{section.icon}</div>
                    <h3 className="text-white font-extrabold">{section.title}</h3>
                  </div>
                  <ul className="space-y-2">
                    {section.points.map((p) => (
                      <li key={p} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                        <span className="text-white/55 text-sm">{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Case Studies ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-950">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.35em] text-center mb-3 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Our Core Expertise & Case Studies</p>
          <h2 className="text-4xl font-black text-white text-center mb-4">High-Impact Campaigns</h2>
          <p className="text-white/45 text-base text-center max-w-xl mx-auto mb-14">
            Real results from government, corporate, and political campaigns across India.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {caseStudies.map((c) => (
              <div key={c.title} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-purple-500/30 transition-colors duration-300 group">
                {/* Image header */}
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={c.image}
                    alt={c.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/40 to-transparent" />
                  <div className="absolute top-3 left-3 w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: c.color }}>{c.icon}</div>
                </div>
                {/* Content */}
                <div className="p-6">
                  <h3 className="text-white font-extrabold leading-snug mb-4">{c.title}</h3>
                  <ul className="space-y-2">
                    {c.details.map((d) => (
                      <li key={d} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-pink-400 flex-shrink-0 mt-0.5" />
                        <span className="text-white/55 text-sm">{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA Banner ── */}
      <div className="relative bg-gradient-to-r from-purple-900 via-purple-800 to-pink-900 py-20 px-4 overflow-hidden text-white text-center">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="relative max-w-3xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.3em] text-purple-300 mb-4">Ready to Grow?</p>
          <h2 className="text-4xl sm:text-5xl font-black mb-6">Start Your Campaign Today</h2>
          <p className="text-white/65 text-lg mb-10 max-w-xl mx-auto">From political outreach to corporate brand building — let's build a strategy that delivers real results.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate("/contact")} className="inline-flex items-center justify-center px-8 py-4 text-sm font-black uppercase tracking-wider rounded-lg bg-white text-purple-900 hover:bg-purple-50 transition-all duration-300 hover:scale-105 shadow-xl">
              Get a Free Strategy Call <ArrowRight className="w-4 h-4 ml-2" />
            </button>
            <button onClick={() => navigate("/social-media")} className="inline-flex items-center justify-center px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-lg border-2 border-white/30 text-white hover:border-white/60 transition-all duration-300">
              Browse All Services
            </button>
          </div>
        </div>
      </div>

      <PortfolioFooter />
    </div>
  );
};

export default BrandingLanding;
